// ============================================================================
// Firebase Cloud Messaging (FCM) 用ラッパ
// ============================================================================
// Phase 4a で Firebase Auth / Functions は AWS Cognito / API Gateway に移行済み。
// FCM はバックエンド（chloe-functions の _shared/fcm.ts）と整合させるため引き続き利用する。
// 旧構成との違い：
//   - initializeApp に projectId と messagingSenderId のみ渡せばよい（auth/functions は不要）
//   - getMessaging は Service Worker 登録済みかつ Push API が使える環境でのみ初期化
// ----------------------------------------------------------------------------

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from "firebase/messaging";

/** requestFcmToken の失敗理由を識別する型 */
export type FcmTokenResult =
  | { ok: true; token: string }
  | { ok: false; reason: FcmFailReason; detail?: string };

export type FcmFailReason =
  | "messaging_not_initialized"   // Firebase Messaging が初期化されていない
  | "push_unsupported"             // ブラウザ自体が Push API 非対応（iOS<16.4 など）
  | "permission_denied"            // ユーザーが過去に拒否、または今回拒否
  | "permission_default"           // プロンプトを閉じた / まだ未許可
  | "vapid_missing"                // VAPID Key が設定されていない
  | "token_empty"                  // getToken は成功したがトークンが空
  | "unknown";                     // 例外

const firebaseMessagingConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

/**
 * Firebase Messaging を初期化。
 *
 * Push API がブラウザでサポートされていない場合（iOS 16.3 以前 / 非対応ブラウザ）は
 * 黙って null を返す。
 * 認証はもう Firebase 経由ではないので、apiKey/projectId が空でも警告だけ出して null を返す。
 */
export async function initFirebaseMessaging(): Promise<{
  app: FirebaseApp;
  messaging: Messaging | null;
} | null> {
  if (app && messaging !== null) {
    return { app, messaging };
  }

  if (!firebaseMessagingConfig.projectId || !firebaseMessagingConfig.messagingSenderId) {
    console.warn("[FCM] 設定が見つかりません。プッシュ通知は無効化されます。", {
      projectId: firebaseMessagingConfig.projectId,
      messagingSenderId: firebaseMessagingConfig.messagingSenderId,
    });
    return null;
  }

  // ブラウザが Push API / Service Worker をサポートしているか確認
  // iOS Safari 16.3 以前、Firefox プライベートモードなどは false を返す
  const supported = await isSupported().catch((err) => {
    console.warn("[FCM] isSupported() 例外:", err);
    return false;
  });
  if (!supported) {
    console.warn("[FCM] このブラウザは Push 通知に対応していません（iOS<16.4 or 非対応環境）");
    return null;
  }

  try {
    app = initializeApp(firebaseMessagingConfig);
    messaging = getMessaging(app);
    return { app, messaging };
  } catch (err) {
    console.warn("[FCM] Messaging 初期化に失敗:", err);
    return null;
  }
}

/**
 * FCM トークン取得。
 * 失敗時は理由を識別できる構造化結果を返すので、呼び出し側で UI ガイダンスを出し分けられる。
 */
export async function requestFcmToken(): Promise<FcmTokenResult> {
  if (!messaging) {
    return { ok: false, reason: "messaging_not_initialized" };
  }

  // 現在の permission 状態を先にチェック
  // 既に "denied" の場合、requestPermission() はプロンプトを出さず即 denied を返す
  if (Notification.permission === "denied") {
    return { ok: false, reason: "permission_denied", detail: "previously denied" };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission === "denied") {
      return { ok: false, reason: "permission_denied" };
    }
    if (permission !== "granted") {
      // "default": ユーザーがプロンプトを閉じた / 何もしなかった
      return { ok: false, reason: "permission_default" };
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || "";
    if (!vapidKey) {
      return { ok: false, reason: "vapid_missing" };
    }

    // Service Worker が active になるまで待つ（iOS で getToken が早すぎると失敗する）
    if ("serviceWorker" in navigator) {
      try {
        await navigator.serviceWorker.ready;
      } catch (err) {
        console.warn("[FCM] serviceWorker.ready 失敗:", err);
      }
    }

    const currentToken = await getToken(messaging, { vapidKey });
    if (!currentToken) {
      return { ok: false, reason: "token_empty" };
    }
    return { ok: true, token: currentToken };
  } catch (err) {
    console.error("[FCM] getToken 例外:", err);
    return {
      ok: false,
      reason: "unknown",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

/** フォアグラウンドメッセージのリスナーを登録 */
export function onForegroundMessage(
  callback: (payload: { title: string; body: string }) => void
): (() => void) | null {
  if (!messaging) return null;

  const unsubscribe = onMessage(messaging, (payload) => {
    const title = payload.data?.title || payload.notification?.title || "クロエ見守り通知";
    const body = payload.data?.body || payload.notification?.body || "";
    callback({ title, body });
  });

  return unsubscribe;
}
